import React from 'react';
import { FormattedMessage } from 'react-intl';
import Dotdotdot from 'react-dotdotdot';
import ProfileInfo from '@xingternal/profile-info';
import { Card } from '@xingternal/card';
import {
  getTrackingUrl,
  trackEmployeesAddContactButtonAction,
  ENTITY_PAGE_MAIN_EMPLOYEES,
} from '@xingternal/pages-tracking';
import styles from './EmployeesCardVerticalContainer.css';
import { capitalizeString } from '@xingternal/pages-common';
import type { ButtonInternal } from '@xingternal/pages-common';
import { addContactButtonDisabled } from '../../utils';
import { BASIC_FLAG, MODERATOR_PREMIUM_FLAG, EXECUTIVE } from '../config';
import ButtonsContainer from '../../../common/components/ButtonsContainer/ButtonsContainer';
type Props = {
  profileImage: {
    src: string;
    type: string;
    profileName: string;
    alt: string;
    connectionDegree?: number;
  };
  profileUrl: string;
  profileName: string;
  position?: string;
  relationship: string;
  authenticated: boolean;
  isCurrentUser?: boolean;
  flag?: string;
  addContactButtonDisabled: typeof addContactButtonDisabled;
  sendMessage: () => void;
  addContact: () => boolean | void;
};

const EmployeesCardVertical = ({
  profileImage,
  profileName,
  position,
  profileUrl,
  relationship,
  authenticated,
  isCurrentUser,
  sendMessage,
  addContact,
  addContactButtonDisabled,
  flag,
}: Props) => {
  const getButtons = (isContact: boolean): ButtonInternal[] => {
    if (isCurrentUser) {
      return [];
    } else if (isContact) {
      return [
        {
          className: styles.employeesButton,
          size: 'small',
          variant: 'secondary',
          children: <FormattedMessage id="EP_EMPLOYEES_BUTTON_SEND_MESSAGE" />,
          onClick: sendMessage,
        },
      ];
    } else {
      return [
        {
          className: styles.employeesButton,
          size: 'small',
          variant: 'secondary',
          children: <FormattedMessage id="EP_EMPLOYEES_BUTTON_ADD_CONTACT" />,
          onClick: () => {
            addContact();
            trackEmployeesAddContactButtonAction(ENTITY_PAGE_MAIN_EMPLOYEES);
          },
          disabled: addContactButtonDisabled(relationship),
          'data-testid': 'add-employee',
        },
      ];
    }
  };

  const getFlag = (flag?: string) => {
    if (!flag || flag === BASIC_FLAG || flag === EXECUTIVE) return;
    if (flag === MODERATOR_PREMIUM_FLAG)
      return {
        size: 'small',
        children: 'Moderator',
        variant: 'moderator',
        className: styles.flag,
      };
    return {
      size: 'small',
      children: flag && capitalizeString(flag),
      variant: flag?.toLowerCase(),
      className: styles.flag,
    };
  };

  const buttons = getButtons(relationship === 'CONTACT');
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const trackingUrl = getTrackingUrl({
      to: profileUrl,
      href: profileUrl,
      trackingKey: `viewEmployeesProfileMain`,
    });
    window.location.href = trackingUrl;
  };

  return (
    <Card className={styles.card}>
      <ProfileInfo
        vertical
        profileImage={{
          src: profileImage.src,
          type: profileImage.type,
          profileName: profileImage.profileName,
          alt: `${profileImage.profileName}'s profile picture`,
          connectionDegree: authenticated ? profileImage.connectionDegree : 0,
          style: { fill: 'none' },
        }}
        flag={getFlag(flag)}
        profileUrl={profileUrl}
        headline={{
          size: 'small',
          as: 'strong',
          children: (
            <Dotdotdot
              clamp={2}
              tagName="span"
              className={flag !== BASIC_FLAG ? styles.headline : undefined}
            >
              {profileName}
            </Dotdotdot>
          ),
        }}
        textBody={[
          {
            size: 'small',
            children: (
              <Dotdotdot tagName="span" clamp={2}>
                {position}
              </Dotdotdot>
            ),
          },
        ]}
        onClick={handleClick}
      />
      <ButtonsContainer className={styles.buttonsContainer} buttons={buttons} />
    </Card>
  );
};

export default EmployeesCardVertical;
